/**
 * TimeTrackingService - Time tracking management with HttpService integration
 */

import httpService, { HttpError } from '../../../core/services/HttpService';

// Endpoint Kandidaten (neues vs. altes Schema)
export const TT_ENDPOINTS = ['/api/time-tracking/timetrackings/'] as const;
export const TT_CR_ENDPOINT = '/api/time-tracking/change-requests/';

async function requestWithFallback<T>(
  method: 'get'|'post'|'patch'|'delete',
  buildPath: (base:string)=>string,
  options?: any
): Promise<T> {
  let lastError: any;
  for (const base of TT_ENDPOINTS) {
    try {
      const url = buildPath(base);
      // Map method to proper httpService signature
      if (method === 'get') {
        const res = await (httpService.get as any)(url, options?.config ?? options);
        return res.data as T;
      }
      if (method === 'delete') {
        const res = await (httpService.delete as any)(url, options?.config ?? options);
        return res.data as T;
      }
      if (method === 'post') {
        const res = await (httpService.post as any)(url, options?.data, options?.config);
        return res.data as T;
      }
      if (method === 'patch') {
        const res = await (httpService.patch as any)(url, options?.data, options?.config);
        return res.data as T;
      }
      const res = await (httpService as any)[method](url, options?.data, options?.config ?? options);
      return (res?.data ?? res) as T;
    } catch (e:any) {
      lastError = e;
      const status = (e as HttpError)?.status;
      // Bei 404 nächsten Kandidaten probieren; andere Fehler sofort weiterwerfen
      if (status !== 404) throw e;
    }
  }
  throw lastError;
}

/**
 * Interface for break period within time tracking
 */
export interface BreakPeriod {
  id?: number;
  start_time: string;
  end_time: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for change request data included in time tracking
 */
export interface ChangeRequestData {
  id: number;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  new_start_time?: string | null;
  new_end_time?: string | null;
  employee_notes?: string | null;
  original_start_time?: string | null;
  original_end_time?: string | null;
  original_work_area?: number | null;
  requested_work_area?: number | null;
}

/**
 * Interface for time tracking object structure
 */
export interface TimeTracking {
  id: number;
  employee: number;
  work_area: number;
  work_area__name?: string;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  breakperiods?: BreakPeriod[];
  
  // Change request metadata (when include_change_requests=true)
  has_pending_change_request?: boolean;
  has_approved_change_request?: boolean;
  change_request_count?: number;
  active_change_request?: ChangeRequestData | null;
  
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for time tracking creation payload
 */
export interface TimeTrackingCreatePayload {
  employee: string | number;
  work_area: string | number;
  start_time: string;
  end_time: string;
  break_start?: string | null;
  break_end?: string | null;
  idempotency_key?: string;
  created_via?: 'employee_app' | 'station_app' | 'admin_desk' | 'import_file';
  import_batch_id?: string | null;
}

/**
 * Interface for time tracking update payload
 */
export interface TimeTrackingUpdatePayload {
  employee?: string | number;
  work_area?: string | number;
  start_time?: string;
  end_time?: string;
  break_start?: string | null;
  break_end?: string | null;
}

/**
 * Interface for time tracking fetch options
 */
export interface TimeTrackingFetchOptions {
  employee?: number;
  work_area?: number;
  start_date?: string;
  end_date?: string;
  include_change_requests?: boolean;
  [key: string]: any;
}

/**
 * Time Tracking Service
 * Centralized service for time tracking-related API operations using HttpService
 */
class TimeTrackingService {
  /**
   * Fetch all time trackings with optional filtering
   * @param options - Optional filtering parameters
   * @returns Promise that resolves to time trackings array
   */
  static async fetchTimeTrackings(options?: TimeTrackingFetchOptions): Promise<TimeTracking[]> {
    return requestWithFallback<TimeTracking[]>(
      'get',
      (base)=> base,
      { params: options || {} }
    );
  }

  /**
   * Fetch time trackings with change request data (optimized version)
   * @param options - Optional filtering parameters
   * @returns Promise that resolves to time trackings array with change request metadata
   */
  static async fetchTimeTrackingsWithChangeRequests(options?: TimeTrackingFetchOptions): Promise<TimeTracking[]> {
    try {
      // Try the optimized endpoint first
      const optimizedOptions = {
        ...options,
        include_change_requests: true
      };
      
      const data = await requestWithFallback<TimeTracking[]>(
        'get',
        (base)=> base,
        { params: optimizedOptions }
      );
      if (data.length > 0 && data[0].hasOwnProperty('has_pending_change_request')) {
        return data;
      }
      
      // If backend doesn't support optimized endpoint yet, fall back to old method (silent)
      return this.fetchTimeTrackingsWithChangeRequestsFallback(options);
      
    } catch (error) {
      // If optimized endpoint fails, fall back to old method (silent)
      return this.fetchTimeTrackingsWithChangeRequestsFallback(options);
    }
  }

  /**
   * Fallback method: Fetch time trackings and their change requests separately (N+1 pattern)
   * This will be removed once backend implements the optimized endpoint
   * @param options - Optional filtering parameters
   * @returns Promise that resolves to time trackings array with change request metadata
   */
  private static async fetchTimeTrackingsWithChangeRequestsFallback(options?: TimeTrackingFetchOptions): Promise<TimeTracking[]> {
    // Import here to avoid circular dependency
    const { default: ChangeRequestService } = await import('./ChangeRequestService');

    // Fetch time trackings first
    const timeTrackings = await this.fetchTimeTrackings(options);

    if (timeTrackings.length === 0) {
      return timeTrackings;
    }

    // Bulk fetch all pending and approved CRs in 2 calls (instead of 2N)
    let allPending: any[] = [];
    let allApproved: any[] = [];
    try {
      [allPending, allApproved] = await Promise.all([
        ChangeRequestService.fetchAllByStatus('PENDING'),
        ChangeRequestService.fetchAllByStatus('APPROVED')
      ]);
    } catch (error) {
      console.warn('Failed to bulk fetch change requests:', error);
    }

    // Index by time_tracking ID for O(1) lookup
    const ttIds = new Set(timeTrackings.map(tt => tt.id));
    const pendingByTT: Record<number, any> = {};
    const approvedByTT: Record<number, any> = {};
    for (const cr of allPending) {
      if (ttIds.has(cr.time_tracking)) pendingByTT[cr.time_tracking] = cr;
    }
    for (const cr of allApproved) {
      if (ttIds.has(cr.time_tracking)) approvedByTT[cr.time_tracking] = cr;
    }

    // Enhance time trackings with change request metadata
    return timeTrackings.map(tt => {
      const pendingCR = pendingByTT[tt.id] || null;
      const approvedCR = approvedByTT[tt.id] || null;
      const activeChangeRequest = pendingCR || approvedCR;

      return {
        ...tt,
        has_pending_change_request: !!pendingCR,
        has_approved_change_request: !!approvedCR,
        change_request_count: (pendingCR ? 1 : 0) + (approvedCR ? 1 : 0),
        active_change_request: activeChangeRequest ? {
          id: activeChangeRequest.id,
          status: activeChangeRequest.status as 'PENDING' | 'APPROVED' | 'REJECTED',
          new_start_time: (activeChangeRequest as any).new_start_time,
          new_end_time: (activeChangeRequest as any).new_end_time,
          employee_notes: activeChangeRequest.employee_notes,
          original_start_time: (activeChangeRequest as any).original_start_time,
          original_end_time: (activeChangeRequest as any).original_end_time,
          original_work_area: (activeChangeRequest as any).original_work_area,
          requested_work_area: (activeChangeRequest as any).requested_work_area
        } : null
      };
    });
  }

  /**
   * Fetch a single time tracking by ID
   * @param timeTrackingId - The time tracking ID
   * @returns Promise that resolves to the time tracking
   */
  static async fetchTimeTracking(timeTrackingId: number): Promise<TimeTracking> {
    return requestWithFallback<TimeTracking>(
      'get',
      (base)=> `${base}${timeTrackingId}/`
    );
  }

  /**
   * Create a new time tracking
   * @param payload - Time tracking creation data
   * @returns Promise that resolves to the created time tracking
   */
  static async createTimeTracking(payload: TimeTrackingCreatePayload, headers?: Record<string,string>): Promise<TimeTracking> {
    return requestWithFallback<TimeTracking>(
      'post',
      (base)=> base,
      { data: payload, config: headers ? { headers } : undefined }
    );
  }

  /**
   * Update a time tracking
   * @param timeTrackingId - The time tracking ID
   * @param payload - Time tracking update data
   * @returns Promise that resolves to the updated time tracking
   */
  static async updateTimeTracking(timeTrackingId: number, payload: TimeTrackingUpdatePayload): Promise<TimeTracking> {
    return requestWithFallback<TimeTracking>(
      'patch',
      (base)=> `${base}${timeTrackingId}/`,
      { data: payload }
    );
  }

  /**
   * Delete a time tracking
   * @param timeTrackingId - The time tracking ID
   * @returns Promise that resolves when deletion is complete
   */
  static async deleteTimeTracking(timeTrackingId: number): Promise<void> {
    await requestWithFallback<void>(
      'delete',
      (base)=> `${base}${timeTrackingId}/`
    );
  }
}

export default TimeTrackingService;
